import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { PortfolioPerformanceBackendService } from 'src/app/domain/pp/service/portfolio-performance-backend.service';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FinancialStatementService } from '../common/financial-statement.service';
import { CompanyScheduleOfInvestments, SoiPart } from '../../utils/soi/utils';
import { PortfolioPerformanceDTO } from 'src/app/domain/pp/dto/PortfolioPerformance';

@Injectable()
export class SoiService {
  private _companySOIs = new BehaviorSubject<CompanyScheduleOfInvestments[]>(undefined);

  fsAccounts: FinancialStatementAccount[];
  period: Date;
  fundUuid: string;
  totalSOI: CompanyScheduleOfInvestments;


  constructor(
    private ppBackendService: PortfolioPerformanceBackendService,
    private fsService: FinancialStatementService,
  ) { }

  get companySOIs(): BehaviorSubject<CompanyScheduleOfInvestments[]> {
    return this._companySOIs;
  }

  async calculateSOI(): Promise<void> {
    const { ownershipUuid, selectedPeriod, financialStatement: { tb } } = this.fsService;
    this.fundUuid = ownershipUuid;
    this.period = selectedPeriod;
    this.fsAccounts = tb;
    if (!this.fundUuid || !this.period || !this.fsAccounts) {
      return;
    }
    this.totalSOI = this.generateNewTotalSOI();
    const newCompanySOIs = await this.generateSOIsFromPPs();
    this.companySOIs.next(newCompanySOIs);
  }

  private createCompanySOI(pp: PortfolioPerformanceDTO): CompanyScheduleOfInvestments {
    const newCompanySOI = new CompanyScheduleOfInvestments(
      pp.name,
      pp.companyId,
      new SoiPart(this.generateAccountCode(true, pp.codeCost), this.generateAccountCode(false, pp.codeCost)),
      new SoiPart(this.generateAccountCode(true, pp.codeUnrealizedPnL, pp.codeInterestAccPnL),
        this.generateAccountCode(false, pp.codeUnrealizedPnL, pp.codeInterestAccPnL)),
      new SoiPart(this.generateAccountCode(true, pp.codeRealizedPnL), this.generateAccountCode(false, pp.codeRealizedPnL)),
    );
    this.setValues(newCompanySOI, pp);
    return newCompanySOI;
  }

  /* eslint-disable */
  private async generateSOIsFromPPs(): Promise<CompanyScheduleOfInvestments[]> {
    const newCompanySOIs: CompanyScheduleOfInvestments[] = [];
    const allPP = await this.ppBackendService.getAll(this.fundUuid, this.period, true);
    if (!allPP?.length) {
      return;
    }
    allPP.forEach((pp) => {
      const existingSOI = newCompanySOIs.find((soi) => soi.companyId === pp.companyId);
      if (existingSOI) {
        this.setValues(existingSOI, pp);
      } else {
        newCompanySOIs.push(this.createCompanySOI(pp));
      }
    });
    return newCompanySOIs;
  }

  private setValues(soi: CompanyScheduleOfInvestments, pp: PortfolioPerformanceDTO): void {
    if (pp.isRealized) {
      this.setValuesForRealized(soi, pp);
    } else {
      this.setValuesForUnrealized(soi, pp);
    }
  }

  private setValuesForRealized(soi: CompanyScheduleOfInvestments, pp: PortfolioPerformanceDTO): void {
    soi.realizedSOI.setPortfolio((pp.fairValue ?? 0) - (pp.cost ?? 0));
    soi.realizedSOI.setBooks(this.getAccountBalanceOrZero(pp.codeRealizedPnL, true));
    this.totalSOI.realizedSOI.setPortfolio(this.totalSOI.realizedSOI.portfolio + (soi.realizedSOI.portfolio ?? 0));
    this.totalSOI.realizedSOI.setBooks(this.totalSOI.realizedSOI.books + this.getAccountBalanceOrZero(pp.codeRealizedPnL, true));
  }

  private setValuesForUnrealized(soi: CompanyScheduleOfInvestments, pp: PortfolioPerformanceDTO): void {
    soi.investmentSOI.setPortfolio(pp.cost ?? 0);
    soi.unrealizedSOI.setPortfolio((pp.fairValue ?? 0) - (pp.cost ?? 0));
    soi.investmentSOI.setBooks(this.getAccountBalanceOrZero(pp.codeCost, false));
    soi.unrealizedSOI.setBooks(pp.codeInterestAccPnL ?
      this.getAccountBalanceOrZero(pp.codeInterestAccPnL, true) + this.getAccountBalanceOrZero(pp.codeUnrealizedPnL, true) :
      this.getAccountBalanceOrZero(pp.codeUnrealizedPnL, true));
    this.totalSOI.investmentSOI.setPortfolio(this.totalSOI.investmentSOI.portfolio + (pp.cost ?? 0));
    this.totalSOI.investmentSOI.setBooks(this.totalSOI.investmentSOI.books + this.getAccountBalanceOrZero(pp.codeCost, false));
    this.totalSOI.unrealizedSOI.setPortfolio(this.totalSOI.unrealizedSOI.portfolio + (pp.fairValue ?? 0) - (pp.cost ?? 0));
    this.totalSOI.unrealizedSOI.setBooks(this.totalSOI.unrealizedSOI.books + (pp.codeInterestAccPnL ?
      this.getAccountBalanceOrZero(pp.codeInterestAccPnL, true) + this.getAccountBalanceOrZero(pp.codeUnrealizedPnL, true) :
      this.getAccountBalanceOrZero(pp.codeUnrealizedPnL, true)));
  }

  private generateAccountCode(isPortfolio: boolean, code: string, secondaryCode?: string): string {
    let finalizedCode: string;
    if (isPortfolio && secondaryCode) {
      finalizedCode = code + ' + ' + secondaryCode;
    } else if (isPortfolio) {
      finalizedCode = code;
    } else if (secondaryCode && this.getBooksAccountNum(secondaryCode) !== '-') {
      finalizedCode = this.getBooksAccountNum(code) + ' + ' + this.getBooksAccountNum(secondaryCode);
    } else {
      finalizedCode = this.getBooksAccountNum(code);
    }
    return finalizedCode;
  }

  private getAccountBalanceOrZero(accountCode: string, isPnL: boolean): number {
    return isPnL ? (this.fsAccounts.find((a) => a.code === accountCode)?.sums.balance ?? 0) / -1 :
      this.fsAccounts.find((a) => a.code === accountCode)?.sums.balance ?? 0;
  }

  private getBooksAccountNum(accountCode: string): string {
    return this.fsAccounts.find((a) => a.code === accountCode)?.code ?? '-';
  }

  private generateNewTotalSOI(): CompanyScheduleOfInvestments {
    return new CompanyScheduleOfInvestments(
      'total',
      null,
      new SoiPart(null, null),
      new SoiPart(null, null),
      new SoiPart(null, null));
  }

}

